import React, { createContext, useContext, useState } from 'react'
import { EmptyState, LoadingState } from '../../empty-state/empty-state'
import { IconSearch, IconTable } from '../../icon/icon'

// The grid's two "no rows" cases:
//   'empty'    -- there is nothing yet (the page's own zero-state copy + action)
//   'no-match' -- rows may exist, but the search or filters hide all of them
//                 (a "clear search and filters" action)
export const OVERLAY_EMPTY = 'empty'
export const OVERLAY_NO_MATCH = 'no-match'

export const DEFAULT_EMPTY_STATE = {
  Icon: IconTable,
  title: 'Nothing here yet',
  message: 'Items appear here once they are added.'
}

export const DEFAULT_NO_MATCH_STATE = {
  Icon: IconSearch,
  title: 'No matches',
  message: 'Nothing matches your search or filters.'
}

// Decide which case a grid showing zero rows is in. A search (server-side or
// the quick filter) or any column filter means the zero is the viewer's
// narrowing, not an empty data set.
export const resolveOverlayCase = ({ searchActive, filterActive }) => (searchActive || filterActive ? OVERLAY_NO_MATCH : OVERLAY_EMPTY)

// AG Grid renders overlay components inside AgGridReact's React tree (via a
// portal), so the owning table hands its state down through context: the
// overlay re-renders when the case or copy changes without re-registering the
// component with the grid.
export const GridOverlayContext = createContext({})

export const GridNoRowsView = ({ overlayCase, emptyState, noMatchState, onClear, animate = true }) => {
  if (overlayCase === OVERLAY_NO_MATCH) {
    const state = { ...DEFAULT_NO_MATCH_STATE, ...(noMatchState || {}) }
    const action = onClear ? { label: 'Clear search and filters', onClick: onClear } : undefined
    return <EmptyState testID="grid-no-match" action={action} animate={animate} {...state} />
  }

  const state = { ...DEFAULT_EMPTY_STATE, ...(emptyState || {}) }
  return <EmptyState testID="grid-empty" animate={animate} {...state} />
}

// noRowsOverlayComponent for TableAG.
// `shouldAnimate` (from TableAG) is asked once per mount, so the entrance
// animation plays when a zero state first appears -- not again when AG Grid
// rebuilds the overlay component on a later poll.
export const GridNoRowsOverlay = () => {
  const context = useContext(GridOverlayContext)
  const { shouldAnimate } = context
  const [animate] = useState(() => (shouldAnimate ? shouldAnimate() : true))
  return <GridNoRowsView {...context} animate={animate} />
}

// loadingOverlayComponent for TableAG: skeleton rows, not a blank grid.
export const GridLoadingOverlay = () =>
  <div className="w-100 h-100 bg-body">
    <LoadingState rows={8} />
  </div>
